import parsePhoneNumber, {
  CountryCode,
  PhoneNumber,
} from 'libphonenumber-js/max'
import { z } from 'zod'
import { Customer } from './types'

export type ParsedPhone = {
  type: 'phone'
  value: PhoneNumber
}

export type ParsedEmail = {
  type: 'email'
  value: string
}

export type ParsedQr = {
  type: 'qr'
  value: string
}

const MASK_SYMBOL = ' • '

const maskTextByLength = (text: string) => {
  if (text.length === 1) {
    return MASK_SYMBOL
  }
  if (text.length <= 3) return text[0] + MASK_SYMBOL.repeat(text.length - 1)

  let charsToHide = 3

  if (text.length <= 6) charsToHide = 2
  if (text.length >= 10) charsToHide = 4
  const toShow = text.length - charsToHide
  const startOffset = Math.ceil(toShow / 2)
  const endOffset = toShow - startOffset
  return (
    text.slice(0, startOffset) +
    MASK_SYMBOL.repeat(charsToHide) +
    text.slice(-endOffset)
  )
}

const maskText = (text: string) => {
  const result = maskTextByLength(text)
  return result.replace(/ {2,}/g, ' ')
}

export type ParsedLogin = ParsedPhone | ParsedEmail | ParsedQr

export const LoginUtil = {
  parsePhone: (number: string, defaultCountry?: CountryCode) =>
    parsePhoneNumber(number, {
      defaultCountry,
      extract: false,
    }),

  maskValue: (value: string) => {
    return maskText(value)
  },

  isPhone: (
    value: string,
    defaultCountry?: CountryCode
  ): ParsedPhone | undefined => {
    const isPhone = LoginUtil.parsePhone(value, defaultCountry)

    if (isPhone?.isValid()) {
      return {
        type: 'phone',
        value: isPhone,
      }
    }
  },
  isEmail: (value: string): ParsedEmail | undefined => {
    const isEmail = z.string().email().safeParse(value)

    if (isEmail.success) {
      return {
        type: 'email',
        value: isEmail.data,
      }
    }
  },
  isPhoneOrEmail: (
    value: string,
    defaultCountry?: CountryCode
  ): ParsedLogin | undefined => {
    return LoginUtil.isPhone(value, defaultCountry) || LoginUtil.isEmail(value)
  },

  /**
   * This just assumes that if the value is not a phone or email, it's a QR code
   */
  parseLogin: (value: string, defaultCountry?: CountryCode) => {
    const parsed = LoginUtil.isPhoneOrEmail(value, defaultCountry)
    if (parsed) {
      return parsed
    }
    return {
      type: 'qr',
      value,
    } as ParsedQr
  },

  maskCustomer: (customer: Customer) => {
    const mobileParsed = customer.mobile
      ? LoginUtil.parsePhone(customer.mobile)
      : null
    const mobile = mobileParsed ? LoginUtil.maskPhone(mobileParsed) : null
    const email = customer.email ? LoginUtil.maskEmail(customer.email) : null
    const phone = customer.phone ? LoginUtil.maskValue(customer.phone) : null
    return {
      ...customer,
      mobile,
      phone,
      email,
    } satisfies Customer
  },

  maskPhone: (phone: PhoneNumber) => {
    const formatted = phone.formatInternational()
    const split = formatted?.split(' ')

    return split
      .map((part, index) => {
        if (index === 0) {
          return part
        }
        if (split.length >= 4 && index === 1) {
          return part
        }
        if (index === split.length - 1 && part.length > 3) {
          const result = MASK_SYMBOL.repeat(part.length - 3) + part.slice(-3)
          return result.replace(/ {2,}/g, ' ')
        }
        if (index === split.length - 1) {
          return part
        }

        const result = MASK_SYMBOL.repeat(part.length)
        return result.replace(/ {2,}/g, ' ')
      })
      .join(' ')
  },
  maskEmail: (email: string) => {
    if (!LoginUtil.isEmail(email)) return email

    const [username, hostname] = email.split('@')
    const [host, domain] = hostname.split('.')

    return maskText(username) + '@' + maskText(host) + '.' + domain
  },
}
